import React, { FC, useEffect, useState } from 'react';
import TicketTable from './TicketTable';
import useTicket from 'src/hooks/api/useTicket';
import Ticket, { TicketClosedReason, TicketSearchCriteria } from 'src/interfaces/api/Ticket';
import { unique } from 'src/utils/ArrayUtils';
import useEmployeeAccess from 'src/hooks/api/useEmployeeAccess';
import Employee from 'src/interfaces/api/Employee';

interface Props {
  showCustomerSatisfaction?: boolean;
  hidden: boolean;
  siteId?: string;
  domainId?: string;
  startDate?: string;
  endDate?: string;
}

const TicketTableContainer: FC<Props> = ({
  domainId,
  siteId,
  endDate,
  startDate,
  showCustomerSatisfaction,
  ...props
}) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [closedReasons, setClosedReasons] = useState<TicketClosedReason[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string>();
  const [totalInterventionsDuration, setTotalInterventionsDuration] = useState(0);
  const [totalInterventionsDurationOnSite, setTotalInterventionsDurationOnSite] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [limit] = useState(10);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const { search, getClosedReasons } = useTicket();
  const { getByIds } = useEmployeeAccess();

  const getEmployeesFromTickets = (tickets: Ticket[]) => {
    const cachedIds = employees.map((employee) => employee.id);
    let supportEmployeesIds = unique(tickets.map((ticket) => ticket.supportEmployeeId));
    supportEmployeesIds = supportEmployeesIds.filter((id) => !cachedIds.includes(id));

    if (supportEmployeesIds.length > 0 && !employeesLoading) {
      setEmployeesError(undefined);
      setEmployeesLoading(true);
      getByIds(supportEmployeesIds)
        .then((collection) => {
          setEmployees([...employees, ...collection.items]);
          setEmployeesLoading(false);
        })
        .catch((e) => {
          setEmployeesError(e.message);
          setEmployeesLoading(false);
        });
    }
  };

  useEffect(() => {
    getClosedReasons().then((collections) => {
      setClosedReasons(collections.items);
    });
  }, []);

  useEffect(() => {
    if (siteId) {
      setError(undefined);
      setLoading(true);
      const criteria: TicketSearchCriteria = { siteId, domainId, startDate, endDate };

      if (showCustomerSatisfaction) {
        criteria.withRating = true;
      }

      search(criteria, page + 1, limit, 'createdAt', 'desc')
        .then((collection) => {
          setTotal(collection.total);
          setTickets(collection.items);
          setTotalInterventionsDuration(collection.totalInterventionsDuration);
          setTotalInterventionsDurationOnSite(collection.totalInterventionsDurationOnSite);
          getEmployeesFromTickets(collection.items);

          setLoading(false);
        })
        .catch((e) => {
          setError(e.message || 'Erreur');
          setLoading(false);
        });
    }
  }, [siteId, domainId, startDate, endDate, page, limit, showCustomerSatisfaction]);

  useEffect(() => {
    setPage(0);
  }, [domainId, startDate, endDate]);

  if (!siteId) {
    return (
      <span hidden={props.hidden} style={{ marginTop: 15, fontSize: 13 }}>
        Aucun ticket pour ce client.
      </span>
    );
  }

  return (
    <TicketTable
      totalInterventionsDurationOnSite={totalInterventionsDurationOnSite}
      totalInterventionsDuration={totalInterventionsDuration}
      tickets={tickets}
      loading={loading}
      error={error}
      page={page}
      setPage={setPage}
      limit={limit}
      total={total}
      employees={employees}
      employeesLoading={employeesLoading}
      employeesError={employeesError}
      closedReasons={closedReasons}
      showCustomerSatisfaction={showCustomerSatisfaction}
      {...props}
    />
  );
};

export default React.memo(TicketTableContainer);
